import API_URLS from '../../../config/apiConfig';
import type {
  FeatureFlagAdminStoreUser,
  FeatureFlagAdminStoreUsersResponse,
  FeatureFlagUserOptionsRequest,
} from '../../../types/features/featureFlags';
import { apiGet } from '../../../utils/apiUtils';

const FEATURE_FLAG_USERS_PAGE_SIZE = 100;

const buildStoreUsersUrl = ({ page, pageSize, search }: FeatureFlagUserOptionsRequest): string => {
  const searchParams = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (search?.trim()) {
    searchParams.set('search', search.trim());
  }

  return `${API_URLS.ADMIN_STORE_USERS}?${searchParams.toString()}`;
};

const normalizeStoreUsersResponse = (
  response: unknown,
  page: number,
  pageSize: number,
): FeatureFlagAdminStoreUsersResponse => {
  const data = response as Partial<FeatureFlagAdminStoreUsersResponse> | null;
  const responseUsers = data?.users;
  const users = Array.isArray(responseUsers) ? responseUsers as FeatureFlagAdminStoreUser[] : [];
  const totalUsers = typeof data?.total_users === 'number' ? data.total_users : users.length;
  const totalPages = typeof data?.total_pages === 'number'
    ? data.total_pages
    : Math.max(1, Math.ceil(totalUsers / pageSize));

  return {
    has_next: typeof data?.has_next === 'boolean' ? data.has_next : page < totalPages,
    has_previous: typeof data?.has_previous === 'boolean' ? data.has_previous : page > 1,
    page: typeof data?.page === 'number' ? data.page : page,
    page_size: typeof data?.page_size === 'number' ? data.page_size : pageSize,
    total_pages: totalPages,
    total_users: totalUsers,
    users,
  };
};

const featureFlagUserOptionsService = {
  getFeatureFlagUsers: async ({
    page,
    pageSize = FEATURE_FLAG_USERS_PAGE_SIZE,
    search,
  }: Partial<FeatureFlagUserOptionsRequest> = {}): Promise<FeatureFlagAdminStoreUsersResponse> => {
    const normalizedPage = page && page > 0 ? page : 1;
    const normalizedPageSize = pageSize && pageSize > 0 ? pageSize : FEATURE_FLAG_USERS_PAGE_SIZE;
    const response = await apiGet(buildStoreUsersUrl({
      page: normalizedPage,
      pageSize: normalizedPageSize,
      search,
    }));

    return normalizeStoreUsersResponse(response, normalizedPage, normalizedPageSize);
  },
};

export default featureFlagUserOptionsService;
